/**
 * The narrow-window replacement for the rail (Option C, C1, plan §3.5; below a 936-px viewport): All engines, a native
 * engine `<select>` and an "Add from…" menu that lists the four entry points (Import opens its source list as a
 * sub-menu). Its controls share `data-focus-role` with the rail's, so a resize across the breakpoint keeps focus on the
 * equivalent control (`useFocusRoleMapping`).
 */
import { useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UserFacingEngineId } from '@/core/engines';
import { Button } from '@/components/ui/button';
import { ReasonButton } from '@/components/ui/ReasonButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ImportSourceItems, useImportSourceDetection } from '../../connection-import/ImportSourcesMenu';
import { openImportSource } from '../../connection-import/connectionImportService';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { engineCatalogGroup } from '../state/engineCatalog';
import type { EntryPointsApi } from '../hooks/useEntryPoints';
import { ENTRY_POINTS } from '../start/entryPoints';
import { PASTE_INPUT_ID } from '../paste/PastePaneField';

type Props = {
  engine: DatabaseEngine;
  onPick: (engine: UserFacingEngineId) => void;
  onAllEngines: () => void;
  entries: EntryPointsApi;
  savingReason?: string;
  className?: string;
};

function ImportSubMenu({ entries }: { entries: EntryPointsApi }) {
  const open = entries.importMenuAt === 'compact';
  const detected = useImportSourceDetection(open, 'connection_dialog', 'compact');
  const entry = ENTRY_POINTS.find((candidate) => candidate.id === 'import')!;
  return (
    <DropdownMenuSub open={open} onOpenChange={(next) => (next ? entries.activate('import', 'compact') : entries.closeImportMenu())}>
      <DropdownMenuSubTrigger data-testid={entry.testId} className="gap-2">
        <entry.Icon className="h-4 w-4" aria-hidden />
        {entry.label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-[60vh] overflow-y-auto">
        <ImportSourceItems
          detected={detected}
          onPick={(id) => {
            entries.closeImportMenu();
            openImportSource(id, 'connection_dialog');
          }}
        />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

export function CompactEngineBar({ engine, onPick, onAllEngines, entries, savingReason, className }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Paste opens a field that takes focus (C3): the closing menu hands focus to it, not back to Add from….
  const focusElsewhere = useRef(false);
  return (
    // A fixed part of the height budget (`useDialogLayout`), wrapped rows included: on the narrowest viewports it wraps.
    <div data-dialog-fixed className={cn('@container/compactbar flex flex-wrap items-center gap-1.5 border-b bg-muted/30 px-4 py-1', className)} data-testid="compact-engine-bar">
      {/* The three controls need ~22.5 rem with the label (All engines ~6.75, the select 9, Add from… ~6, the gaps). Only
          where the bar is narrower than that plus a margin for wider system fonts does the label go to assistive tech
          (the arrow and the tooltip stay), so the bar keeps one row — and the 400×300 window its short tier — while the
          label shows wherever it fits (UX-08; UX review r2, R-2). */}
      <Button type="button" variant="ghost" size="sm" title="All engines" className="shrink-0 gap-1.5 px-2 text-[13px] text-muted-foreground" data-focus-role="all-engines" onClick={onAllEngines} data-testid="rail-all-engines-button">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        <span className="@max-[26rem]/compactbar:sr-only">All engines</span>
      </Button>
      <select
        aria-label="Engine"
        data-focus-role="engine"
        value={engine}
        onChange={(event) => onPick(event.target.value as UserFacingEngineId)}
        // Never narrower than "SQL Server" with its arrow (UX-08); it takes the room the icon-only All engines leaves.
        className="h-8 min-w-36 flex-1 rounded-md border border-input bg-background px-2 text-sm font-medium shadow-xs sm:max-w-60"
        data-testid="engine-compact-select"
      >
        <optgroup label="Databases">
          {engineCatalogGroup('databases').map((entry) => <option key={entry.id} value={entry.id}>{entry.label}</option>)}
        </optgroup>
        <optgroup label="Azure services">
          {engineCatalogGroup('azure').map((entry) => <option key={entry.id} value={entry.id}>{entry.label}</option>)}
        </optgroup>
      </select>
      {savingReason ? (
        <ReasonButton type="button" variant="outline" size="sm" className="shrink-0" reason={savingReason} data-focus-role="entry" data-testid="compact-add-from-button">
          Add from…
        </ReasonButton>
      ) : (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="shrink-0" data-focus-role="entry" data-testid="compact-add-from-button">
              Add from…
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="min-w-[16rem]"
            onCloseAutoFocus={(event) => {
              // The field mounted while the menu still trapped focus, so it is focused here, once the menu has let go.
              if (focusElsewhere.current) {
                event.preventDefault();
                document.getElementById(PASTE_INPUT_ID)?.focus();
              }
              focusElsewhere.current = false;
            }}
          >
            {ENTRY_POINTS.filter((entry) => entry.id !== 'import').map((entry) => {
              const reason = entries.reasonFor(entry.id);
              return (
                <DropdownMenuItem
                  key={entry.id}
                  disabled={!!reason}
                  title={reason}
                  className="gap-2"
                  onSelect={() => {
                    focusElsewhere.current = entry.id === 'paste';
                    entries.activate(entry.id, 'compact');
                  }}
                  data-testid={entry.testId}
                >
                  <entry.Icon className="h-4 w-4" aria-hidden />
                  {entry.label}
                </DropdownMenuItem>
              );
            })}
            <ImportSubMenu entries={entries} />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
